/**
 * DriverHelpPage - Pagina de Ajuda do Motorista
 *
 * Funcionalidades:
 * - FAQ
 * - Contato com suporte
 * - Tutoriais
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Button,
} from '@mui/material';
import {
  Help as HelpIcon,
  ExpandMore as ExpandIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  PlayCircle as VideoIcon,
  Article as ArticleIcon,
  ChevronRight as ArrowIcon,
  QuestionAnswer as FaqIcon,
  ContactSupport as SupportIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';

const FAQ_ITEMS = [
  {
    question: 'Como iniciar uma rota?',
    answer: 'Na tela "Rotas", selecione a rota que deseja executar e clique em "Iniciar Rota". O app comecara a rastrear sua posicao e mostrara a proxima parada.',
  },
  {
    question: 'Como registrar uma entrega?',
    answer: 'Ao chegar no local da entrega, clique em "Cheguei". Em seguida, clique em "Registrar Entrega" para tirar fotos, coletar assinatura e finalizar.',
  },
  {
    question: 'E se o cliente nao estiver no local?',
    answer: 'Voce pode clicar em "Pular Entrega" e selecionar o motivo (cliente ausente, endereco incorreto, etc). A entrega sera marcada como nao realizada.',
  },
  {
    question: 'Como usar a navegacao?',
    answer: 'Clique no botao de navegacao ao lado do endereco para abrir o Google Maps ou Waze com a rota ate o destino.',
  },
  {
    question: 'O app funciona offline?',
    answer: 'Sim! O app salva os dados localmente. Quando voce voltar a ter conexao, os dados serao sincronizados automaticamente.',
  },
  {
    question: 'Como pausar uma rota?',
    answer: 'Durante a execucao, clique no botao de pausa no topo da tela. A rota ficara pausada ate voce retomar.',
  },
  {
    question: 'Como alterar minha senha?',
    answer: 'Va ate "Perfil" e clique em "Alterar senha". Digite sua senha atual e a nova senha.',
  },
  {
    question: 'O que fazer se o app travar?',
    answer: 'Tente fechar e reabrir o app. Se o problema persistir, va em Configuracoes > Limpar dados locais. Voce precisara fazer login novamente.',
  },
];

const DriverHelpPage = () => {
  const tokens = usePremiumTokens();
  const [expanded, setExpanded] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/5511999999999?text=Ola, preciso de ajuda com o Fleet Driver', '_blank');
  };

  const handleEmail = () => {
    window.open('mailto:suporte@fleetpremium.com.br?subject=Ajuda Fleet Driver', '_blank');
  };

  const handlePhone = () => {
    window.open('tel:+551199999999', '_blank');
  };

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <HelpIcon sx={{ color: tokens?.accent?.primary }} />
        <Typography
          sx={{
            color: tokens?.text?.primary,
            fontWeight: 700,
            fontSize: '20px',
          }}
        >
          Ajuda
        </Typography>
      </Box>

      {/* Contato Rapido */}
      <Typography
        sx={{
          color: tokens?.text?.tertiary,
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: 1,
          px: 1,
        }}
      >
        Contato Rapido
      </Typography>
      <Card
        sx={{
          mb: 3,
          backgroundColor: tokens?.surface?.raised,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${tokens?.border?.subtle}`,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<WhatsAppIcon />}
              onClick={handleWhatsApp}
              sx={{
                flex: 1,
                borderColor: '#25D366',
                color: '#25D366',
                '&:hover': { borderColor: '#25D366', backgroundColor: '#25D36610' },
              }}
            >
              WhatsApp
            </Button>
            <Button
              variant="outlined"
              startIcon={<PhoneIcon />}
              onClick={handlePhone}
              sx={{ flex: 1 }}
            >
              Ligar
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={handleEmail}
              sx={{ flex: 1 }}
            >
              Email
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* FAQ */}
      <Typography
        sx={{
          color: tokens?.text?.tertiary,
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: 1,
          px: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <FaqIcon sx={{ fontSize: 16 }} />
        Perguntas Frequentes
      </Typography>
      <Card
        sx={{
          mb: 3,
          backgroundColor: tokens?.surface?.raised,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${tokens?.border?.subtle}`,
          overflow: 'hidden',
        }}
      >
        {FAQ_ITEMS.map((item, index) => (
          <Accordion
            key={index}
            expanded={expanded === index}
            onChange={handleAccordionChange(index)}
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: 'transparent',
              '&:before': { display: 'none' },
              '&:not(:last-child)': {
                borderBottom: `1px solid ${tokens?.border?.subtle}`,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandIcon sx={{ color: tokens?.text?.secondary }} />}
              sx={{
                px: 2,
                '& .MuiAccordionSummary-content': { my: 1.5 },
              }}
            >
              <Typography sx={{ color: tokens?.text?.primary, fontSize: '14px', fontWeight: 500 }}>
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
              <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
                {item.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Card>

      {/* Recursos */}
      <Typography
        sx={{
          color: tokens?.text?.tertiary,
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: 1,
          px: 1,
        }}
      >
        Recursos
      </Typography>
      <Card
        sx={{
          backgroundColor: tokens?.surface?.raised,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${tokens?.border?.subtle}`,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <List disablePadding>
            <ListItem
              component="button"
              sx={{
                py: 1.5,
                cursor: 'pointer',
                '&:hover': { backgroundColor: tokens?.surface?.elevated },
              }}
            >
              <ListItemIcon sx={{ minWidth: 44 }}>
                <VideoIcon sx={{ color: tokens?.accent?.primary }} />
              </ListItemIcon>
              <ListItemText
                primary="Tutoriais em video"
                secondary="Aprenda a usar o app"
                primaryTypographyProps={{ fontSize: '15px', color: tokens?.text?.primary }}
                secondaryTypographyProps={{ fontSize: '12px', color: tokens?.text?.tertiary }}
              />
              <ListItemSecondaryAction>
                <ArrowIcon sx={{ color: tokens?.text?.tertiary }} />
              </ListItemSecondaryAction>
            </ListItem>
            <Divider sx={{ borderColor: tokens?.border?.subtle }} />
            <ListItem
              component="button"
              sx={{
                py: 1.5,
                cursor: 'pointer',
                '&:hover': { backgroundColor: tokens?.surface?.elevated },
              }}
            >
              <ListItemIcon sx={{ minWidth: 44 }}>
                <ArticleIcon sx={{ color: tokens?.text?.secondary }} />
              </ListItemIcon>
              <ListItemText
                primary="Manual do usuario"
                secondary="Documentacao completa"
                primaryTypographyProps={{ fontSize: '15px', color: tokens?.text?.primary }}
                secondaryTypographyProps={{ fontSize: '12px', color: tokens?.text?.tertiary }}
              />
              <ListItemSecondaryAction>
                <ArrowIcon sx={{ color: tokens?.text?.tertiary }} />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Footer */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
          Horario de atendimento: Seg-Sex, 8h as 18h
        </Typography>
        <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px', mt: 0.5 }}>
          suporte@fleetpremium.com.br
        </Typography>
      </Box>
    </Box>
  );
};

export default DriverHelpPage;
